import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  CalendarDays,
  Search,
  Users,
  Clock,
  CheckCircle2,
  XCircle,
  Check,
  X,
  Loader2,
  Settings2,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import { formatDateString } from "../CommonJquery/CommonJquery.js";
import {
  get_reservations,
  accept_zomato_booking,
  reject_zomato_booking,
  cancel_zomato_booking,
  server_post_data,
} from "../ServiceConnection/serviceconnection.js";

// ── Constants ──────────────────────────────────────────────────────────────

const BOOKING_STATUS_MAP = {
  0: { label: "Pending", variant: "secondary" },
  1: { label: "Approved", variant: "default" },
  2: { label: "Arrived", variant: "default" },
  3: { label: "Completed", variant: "success" },
  4: { label: "No Show", variant: "warning" },
  5: { label: "Declined", variant: "destructive" },
  6: { label: "Cancelled", variant: "destructive" },
  7: { label: "Reassigned", variant: "secondary" },
  8: { label: "Dispute", variant: "destructive" },
};

const BOOKING_TYPE_MAP = {
  0: "Walk-in",
  1: "Software",
  2: "Website",
  3: "Website",
};
const SEARCH_TYPES = ["Name", "Phone", "Email"];

const canAccept = (r) =>
  r.external_source === "zomato" && r.booking_status === 0;
const canReject = (r) =>
  r.external_source === "zomato" && r.booking_status === 0;
const canCancel = (r) =>
  r.external_source === "zomato" && [0, 1, 2].includes(r.booking_status);

// ── Component ──────────────────────────────────────────────────────────────

export default function Reservations() {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [reservations, setReservations] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("Name");
  const [statusFilter, setStatusFilter] = useState("all");
  const [actionLoading, setActionLoading] = useState({});
  const [cancelModal, setCancelModal] = useState({
    open: false,
    reservation: null,
  });
  const [cancelReason, setCancelReason] = useState("");

  const todayStr = new Date().toISOString().split("T")[0];
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const [startDate, setStartDate] = useState(
    sevenDaysAgo.toISOString().split("T")[0],
  );
  const [endDate, setEndDate] = useState(todayStr);

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const fetchReservations = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", "1");
    fd.append("start_date", startDate || "");
    fd.append("end_date", endDate || "");
    try {
      const response = await server_post_data(get_reservations, fd);
      if (!response.data.error) {
        const data = response.data.message.data_reservation || [];
        setReservations(data);
        applyFilters(data, searchQuery, searchType, statusFilter);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "Failed to load reservations",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, toast]);

  // ── Filters ───────────────────────────────────────────────────────────────

  const applyFilters = useCallback((data, query, type, status) => {
    let result = [...data];
    if (status !== "all")
      result = result.filter((r) => String(r.booking_status) === status);
    if (query.trim()) {
      const q = query.toLowerCase();
      result = result.filter((r) => {
        if (type === "Name") return r.guest_name?.toLowerCase().includes(q);
        if (type === "Phone") return r.guest_mobile_no?.includes(query);
        if (type === "Email") return r.guest_email?.toLowerCase().includes(q);
        return true;
      });
    }
    setFilteredData(result);
  }, []);

  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);
      applyFilters(reservations, query, searchType, statusFilter);
    },
    [reservations, searchType, statusFilter, applyFilters],
  );

  const handleSearchTypeChange = useCallback(
    (type) => {
      setSearchType(type);
      applyFilters(reservations, searchQuery, type, statusFilter);
    },
    [reservations, searchQuery, statusFilter, applyFilters],
  );

  const handleStatusFilterChange = useCallback(
    (status) => {
      setStatusFilter(status);
      applyFilters(reservations, searchQuery, searchType, status);
    },
    [reservations, searchQuery, searchType, applyFilters],
  );

  const handleClearFilters = useCallback(() => {
    setSearchQuery("");
    setStatusFilter("all");
    setFilteredData(reservations);
  }, [reservations]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  // ── Zomato Actions ────────────────────────────────────────────────────────

  const updateLocalStatus = (primary_id, newStatus) => {
    const upd = (list) =>
      list.map((r) =>
        r.primary_id === primary_id ? { ...r, booking_status: newStatus } : r,
      );
    setReservations(upd);
    setFilteredData(upd);
  };

  const setLoading = (id, action) =>
    setActionLoading((p) => ({ ...p, [id]: action }));
  const clearLoading = (id) =>
    setActionLoading((p) => {
      const n = { ...p };
      delete n[id];
      return n;
    });

  const handleAccept = async (r) => {
    setLoading(r.primary_id, "accept");
    const fd = new FormData();
    fd.append("partner_booking_id", r.primary_id);
    try {
      const res = await server_post_data(accept_zomato_booking, fd);
      if (!res.data.error) {
        updateLocalStatus(r.primary_id, 1);
        toast({
          title: "Booking Accepted",
          description: `${r.guest_name} ` + res.data.message,
        });
      } else {
        toast({
          title: "Error",
          description: res.data.message || "Accept failed",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "Network error",
        variant: "destructive",
      });
    } finally {
      clearLoading(r.primary_id);
    }
  };

  const handleReject = async (r) => {
    setLoading(r.primary_id, "reject");
    const fd = new FormData();
    fd.append("partner_booking_id", r.primary_id);
    fd.append("reason", "Unable to accommodate");
    try {
      const res = await server_post_data(reject_zomato_booking, fd);
      if (!res.data.error) {
        updateLocalStatus(r.primary_id, 5);
        toast({
          title: "Booking Rejected",
          description: `${r.guest_name} ` + res.data.message,
        });
      } else {
        toast({
          title: "Error",
          description: res.data.message || "Reject failed",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "Network error",
        variant: "destructive",
      });
    } finally {
      clearLoading(r.primary_id);
    }
  };

  const openCancelModal = (r) => {
    setCancelReason("");
    setCancelModal({ open: true, reservation: r });
  };

  const handleCancelConfirm = async () => {
    const r = cancelModal.reservation;
    if (!r) return;
    setCancelModal((p) => ({ ...p, open: false }));
    setLoading(r.primary_id, "cancel");
    const fd = new FormData();
    fd.append("partner_booking_id", r.primary_id);
    fd.append("reason", cancelReason || "Cancelled by restaurant");
    try {
      const res = await server_post_data(cancel_zomato_booking, fd);
      if (!res.data.error) {
        updateLocalStatus(r.primary_id, 6);
        toast({
          title: "Booking Cancelled",
          description: `${r.guest_name} ` + res.data.message,
        });
      } else {
        toast({
          title: "Error",
          description: res.data.message || "Cancel failed",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "Network error",
        variant: "destructive",
      });
    } finally {
      clearLoading(r.primary_id);
    }
  };

  // ── Stats ─────────────────────────────────────────────────────────────────

  const stats = {
    total: reservations.length,
    pending: reservations.filter((r) => r.booking_status === 0).length,
    completed: reservations.filter((r) => r.booking_status === 3).length,
    cancelled: reservations.filter((r) => [5, 6].includes(r.booking_status))
      .length,
  };

  // Zomato pending count — badge ke liye
  const zomatoPendingCount = reservations.filter(
    (r) => r.external_source === "zomato" && r.booking_status === 0,
  ).length;

  const formatTime = (t) => {
    if (!t) return "—";
    const [h, m] = t.split(":");
    const hour = parseInt(h, 10);
    return `${hour % 12 || 12}:${m} ${hour >= 12 ? "PM" : "AM"}`;
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <MainLayout>
      <Header
        title="Reservations"
        subtitle="View and manage all table reservations"
      />

      <div className="p-6 space-y-6">
        {/* Stats */}
        <Button
          variant="outline"
          className="gap-2 border-orange-400 text-orange-600 hover:bg-orange-50 hover:text-orange-700 relative"
          onClick={() => navigate("/zomato-management")}
        >
          <Settings2 className="w-4 h-4" />
          Zomato Management
          {/* Pending count badge */}
          {zomatoPendingCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center">
              {zomatoPendingCount > 9 ? "9+" : zomatoPendingCount}
            </span>
          )}
        </Button>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">

          {[
            {
              label: "Total",
              value: stats.total,
              icon: CalendarDays,
              color: "primary",
            },
            {
              label: "Pending",
              value: stats.pending,
              icon: Clock,
              color: "warning",
            },
            {
              label: "Completed",
              value: stats.completed,
              icon: CheckCircle2,
              color: "success",
            },
            {
              label: "Cancelled",
              value: stats.cancelled,
              icon: XCircle,
              color: "destructive",
            },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="stat-card">
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-lg bg-${color}/10 flex items-center justify-center`}
                >
                  <Icon className={`w-5 h-5 text-${color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{value}</p>
                  <p className="text-sm text-muted-foreground">{label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={`Search by ${searchType}...`}
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <Select value={searchType} onValueChange={handleSearchTypeChange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Search by" />
            </SelectTrigger>
            <SelectContent>
              {SEARCH_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(BOOKING_STATUS_MAP).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-[150px]"
            />
            <span className="text-muted-foreground text-sm">to</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-[150px]"
            />
            <Button
              onClick={fetchReservations}
              disabled={showLoaderAdmin}
              className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
            >
              {showLoaderAdmin ? "Loading..." : "Apply"}
            </Button>
          </div>

          {(searchQuery || statusFilter !== "all") && (
            <Button variant="outline" onClick={handleClearFilters}>
              Clear
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Guest</TableHead>
                <TableHead className="text-muted-foreground">
                  Date & Time
                </TableHead>
                <TableHead className="text-muted-foreground">Guests</TableHead>
                <TableHead className="text-muted-foreground">
                  Table(s)
                </TableHead>
                <TableHead className="text-muted-foreground">Source</TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
                <TableHead className="text-muted-foreground">Description</TableHead>
                <TableHead className="text-muted-foreground">
                  Booked On
                </TableHead>
                <TableHead className="text-muted-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {showLoaderAdmin ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center text-muted-foreground py-8"
                  >
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center text-muted-foreground py-8"
                  >
                    No reservations found
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((r) => {
                  const status = BOOKING_STATUS_MAP[r.booking_status] ?? {
                    label: "Unknown",
                    variant: "secondary",
                  };
                  const loading = actionLoading[r.primary_id];
                  const isZomato = r.external_source === "zomato";

                  return (
                    <TableRow key={r.primary_id} className="border-border">
                      <TableCell>
                        <div className="font-semibold">
                          {r.guest_name || "—"}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {r.guest_mobile_no || ""}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="text-sm font-medium">
                          {r.book_date ? formatDateString(r.book_date) : "—"}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatTime(r.book_time)}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Users className="w-3.5 h-3.5 text-muted-foreground" />
                          <span>{r.no_of_guest ?? 0}</span>
                          {r.no_of_child > 0 && (
                            <span className="text-xs text-muted-foreground">
                              +{r.no_of_child} child
                            </span>
                          )}
                        </div>
                      </TableCell>

                      <TableCell className="text-sm text-muted-foreground">
                        {r.table_code_names || "—"}
                      </TableCell>

                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`text-xs ${isZomato ? "border-orange-400 text-orange-500 bg-orange-50" : ""}`}
                        >
                          {r.external_source
                            ? r.external_source.charAt(0).toUpperCase() +
                            r.external_source.slice(1)
                            : (BOOKING_TYPE_MAP[r.booking_type] ?? "—")}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        <Badge variant={status.variant} className="text-xs">
                          {status.label}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        {r.reservation_description}
                      </TableCell>

                      <TableCell className="text-sm text-muted-foreground">
                        {r.entry_date ? formatDateString(r.entry_date) : "—"}
                      </TableCell>

                      <TableCell>
                        {isZomato ? (
                          <div className="flex items-center gap-1">
                            {canAccept(r) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 border-green-400 text-green-600 hover:bg-green-50 hover:text-green-700"
                                disabled={!!loading}
                                onClick={() => handleAccept(r)}
                                title="Accept Booking"
                              >
                                {loading === "accept" ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Check className="w-3.5 h-3.5" />
                                )}
                              </Button>
                            )}
                            {canReject(r) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 border-yellow-400 text-yellow-600 hover:bg-yellow-50 hover:text-yellow-700"
                                disabled={!!loading}
                                onClick={() => handleReject(r)}
                                title="Reject Booking"
                              >
                                {loading === "reject" ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <X className="w-3.5 h-3.5" />
                                )}
                              </Button>
                            )}
                            {canCancel(r) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 border-red-400 text-red-600 hover:bg-red-50 hover:text-red-700"
                                disabled={!!loading}
                                onClick={() => openCancelModal(r)}
                                title="Cancel Booking"
                              >
                                {loading === "cancel" ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <XCircle className="w-3.5 h-3.5" />
                                )}
                              </Button>
                            )}
                            {!canAccept(r) &&
                              !canReject(r) &&
                              !canCancel(r) && (
                                <span className="text-xs text-muted-foreground">
                                  —
                                </span>
                              )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            —
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {!showLoaderAdmin && filteredData.length > 0 && (
          <p className="text-sm text-muted-foreground text-right">
            Showing {filteredData.length} of {reservations.length} reservations
          </p>
        )}
      </div>

      {/* Cancel Modal */}
      <Dialog
        open={cancelModal.open}
        onOpenChange={(open) => setCancelModal((p) => ({ ...p, open }))}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Booking</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {cancelModal.reservation && (
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">
                  {cancelModal.reservation.guest_name}
                </span>{" "}
                Do you Want Cancel This Booking (
                {formatDateString(cancelModal.reservation.book_date)},{" "}
                {formatTime(cancelModal.reservation.book_time)})
              </p>
            )}
            <Input
              placeholder="Cancellation reason (optional)"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setCancelModal({ open: false, reservation: null })}
            >
              Back
            </Button>
            <Button variant="destructive" onClick={handleCancelConfirm}>
              Confirm Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
